import { useMemo, useState } from 'react';
import { DataTable, EmptyState, StatusPill, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import { Section } from '@vnetwork/ui/layout';
import { EntityLink } from '@/components/entity-link';
import { paginationView } from '../pagination-summary';
import { useCustomerServices, type ServiceRow } from '@/api/customer-services';
import { formatDate, formatMoney } from '@/lib/format';
import { serviceTone, humanize } from './status-tones';
import { TabError, TabLoading } from './tab-states';

const PAGE_SIZE = 20;

function useServiceColumns() {
  return useMemo<ColumnDef<ServiceRow, unknown>[]>(
    () => [
      {
        id: 'service',
        header: 'Service',
        cell: ({ row }) => (
          <EntityLink
            to={`/services/${row.original.service_id}`}
            id={row.original.service_id}
            name={row.original.service_name}
          />
        ),
      },
      { id: 'product_group_name', header: 'Product Group', cell: ({ row }) => row.original.product_group_name || '—' },
      { id: 'service_entity_name', header: 'Entity', cell: ({ row }) => row.original.service_entity_name || '—' },
      {
        id: 'service_status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusPill tone={serviceTone(row.original.service_status)}>
            {humanize(row.original.service_status)}
          </StatusPill>
        ),
      },
      {
        id: 'service_price_register',
        header: () => <div style={{ textAlign: 'right' }}>Register Price</div>,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>{formatMoney(row.original.service_price_register)}</div>
        ),
      },
      {
        id: 'service_price_recurring',
        header: () => <div style={{ textAlign: 'right' }}>Recurring Price</div>,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>{formatMoney(row.original.service_price_recurring)}</div>
        ),
      },
      { id: 'service_start_date', header: 'Start', cell: ({ row }) => formatDate(row.original.service_start_date) },
      { id: 'service_stopped_date', header: 'Stopped', cell: ({ row }) => formatDate(row.original.service_stopped_date) },
    ],
    [],
  );
}

/** One service list (Official or POC) with its own pagination. */
function ServiceTable({ id, isPoc, title }: { id: number; isPoc: boolean; title: string }) {
  const [page, setPage] = useState(1);
  const columns = useServiceColumns();
  const { data, isLoading, isError, refetch } = useCustomerServices(id, { is_poc: isPoc }, {
    page,
    page_size: PAGE_SIZE,
  });

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  return (
    <Section title={title}>
      {isError ? (
        <TabError title={`Failed to load ${title.toLowerCase()}`} onRetry={() => refetch()} />
      ) : isLoading ? (
        <TabLoading rows={4} />
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          enableSorting={false}
          getRowId={(r) => String(r.service_id)}
          emptyState={<EmptyState title={`No ${title.toLowerCase()}`} />}
          footer={(() => {
            const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
            return (
              <Pagination page={page} pageCount={view.pageCount} onPageChange={setPage} summary={view.summary} />
            );
          })()}
        />
      )}
    </Section>
  );
}

/** Service tab — Official services then POC services, each its own DataTable. */
export function ServiceTab({ id }: { id: number }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <ServiceTable id={id} isPoc={false} title="Official Services" />
      <ServiceTable id={id} isPoc title="POC Services" />
    </div>
  );
}
